import * as React from "react";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	Tooltip,
	Legend,
	CategoryScale,
	BarElement,
	ChartData,
	ChartDataset,
	Title,
	LinearScale,
	Colors,
} from "chart.js";
import { IChartData } from "@/models/Charts/ChartData";
import DataLabels, { Context } from "chartjs-plugin-datalabels";
import rc from "randomcolor";
ChartJS.register(
	Tooltip,
	Legend,
	CategoryScale,
	Colors,
	BarElement,
	LinearScale,
	Title,
	DataLabels
);
export interface IBarChartProps {
	data: IChartData;
	horizontal?: boolean;
	showLegend?: boolean;
	stacked?: boolean;
	title?: string;
	showDataLabel?: boolean;
}

export function BarChart(props: IBarChartProps) {
	const data = React.useMemo<ChartData<"bar", (number | null)[], string>>(() => {
		const datasets = props.data.items.map<ChartDataset<"bar", (number | null)[]>>(
			(i, index, array) => {
				const dataset: ChartDataset<"bar", (number | null)[]> = {
					data: i.values,
					label: i.label,
					datalabels: {
						display: !!props.showDataLabel,
						anchor: "end",
						align: "top",
						formatter(value, context) {
							return i.label;
						},
					},
					backgroundColor:
						i.color ??
						i.values.map((item, index) =>
							rc({
								seed: i.label ?? props.data.labels[index],
								luminosity: "bright",
							})
						),
				};
				return dataset;
			}
		);
		const labels = props.data.labels;
		return {
			datasets: datasets,
			labels: labels,
		};
	}, [props.data]);

	const max = React.useMemo(() => {
		const m = props.data.items.flatMap((i) => i.values.map((i) => i));
		return Math.max(...m) * 1.1;
	}, [props.data.items]);

	return (
		<Bar
			width={"100%"}
			height={"100%"}
			options={{
				indexAxis: props.horizontal ? "y" : "x",
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: true,
						stacked: props.stacked,
						ticks: {
							padding: 15,
						},
						suggestedMax: max,
					},
					x: {
						stacked: props.stacked,
					},
				},
				interaction: {
					mode: "nearest",
					axis: props.horizontal ? "y" : "x",
					intersect: false,
				},
				plugins: {
					legend: {
						display: !!props.showLegend,
					},
					tooltip: {
						mode: "nearest",
					},
					title: {
						display: !!props.title,
						text: props.title,
						align: "center",
					},
					colors: {
						enabled: false,
						forceOverride: true,
					},
				},
			}}
			data={data}
			key={props.data.key}></Bar>
	);
}
